<template>
    <div class="d-flex flex-column gap-6">
        <!-- Loading state with skeleton loaders -->
        <lb-datatablenew
            v-show="loading"
            :data-testid="testIdPrefix + '-table__data-table--loading'"
            :headers="headers"
            :items="shimmerItems"
            :loading="false"
            :enableslot="shimmerSlots"
            class="report-table rounded-lg"
        >
            <template v-for="slot in shimmerSlots" v-slot:[slot]>
                <v-skeleton-loader :key="slot" class="my-1" type="text" />
            </template>
        </lb-datatablenew>
        
        <!-- Data state (v-show keeps it mounted to preserve filter state) -->
        <lb-datatablenew
            v-show="!loading"
            :data-testid="testIdPrefix + '-table__data-table'"
            :headers="headers"
            :items="items"
            :enableslot="dataSlots"
            :loading="loading"
            :bypassFiltering="true"
            class="report-table rounded-lg"
            @update:filters="$emit('filter-change', $event)"
        >
                <!-- Pass through all scoped slots from parent -->
                <template v-for="(_, name) in $scopedSlots" v-slot:[name]="slotData">
                    <slot :name="name" v-bind="slotData" />
                </template>
                
                <!-- Default created_at slot -->
                <template v-if="!$scopedSlots.created_at" v-slot:created_at="{ item }">
                    <div>{{ formatDate(item.created_at) }}</div>
                </template>
                
                <!-- Default status slot -->
                <template v-if="!$scopedSlots.status" v-slot:status="{ item }">
                    <StatusIndicator :variant="item.status" size="sm"/>
                </template>
                
                <!-- Default action slot -->
                <template v-if="!$scopedSlots.action" v-slot:action="{ item }">
                    <div>
                        <div class="d-flex align-center gap-4" :data-testid="testIdPrefix + '-table__actions-row'">
                            <!-- Fixed width container for download button to maintain eye button position -->
                            <div class="action-btn-container">
                                <v-tooltip v-if="item.stage === 1" bottom content-class="tooltip-bottom">
                                    <template v-slot:activator="{ on, attrs }">
                                        <img 
                                            src="/img/icons/DownloadIcon.svg" 
                                            alt="download-icon" 
                                            v-bind="attrs"
                                            v-on="on" 
                                            @click.stop="$emit('download', item._id)" 
                                            class="cursor-pointer pa-1"
                                        />
                                    </template>
                                    <span>Download</span>
                                </v-tooltip>
                            </div>

                            <v-tooltip bottom content-class="tooltip-bottom" :data-testid="testIdPrefix + '-table__view-tooltip'">
                                <template v-slot:activator="{ on, attrs }">
                                    <img 
                                        src="/img/icons/EyeIcon.svg" 
                                        alt="view-icon" 
                                        v-bind="attrs"
                                        v-on="on" 
                                        @click="$emit('view-details', item)" 
                                        class="cursor-pointer pa-1"
                                    />
                                </template>
                                <span :data-testid="testIdPrefix + '-table__view-tooltip-text'">View</span>
                            </v-tooltip>
                        </div>
                    </div>
                </template>
            </lb-datatablenew>
        
        <!-- Pagination -->
        <Pagination
            :value="currentPage"
            :list="[]"
            :itemsperpage="itemsPerPage"
            :lengthOverride="totalPages"
            :hideOnSinglePage="true"
            @input="$emit('page-change', $event)"
            :data-testid="testIdPrefix + '__pagination'"
        />
    </div>
</template>

<script>
import StatusIndicator from '@/modules/varianceanalysis/components/StatusIndicator.vue';
import Pagination from '@/views/common/Pagination.vue';
import { PAGINATION } from '@/modules/varianceanalysis/utils/constants.js';

export default {
    name: 'ReportTable',
    
    components: {
        StatusIndicator,
        Pagination
    },
    
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        headers: {
            type: Array,
            required: true,
        },
        items: {
            type: Array,
            default: () => [],
        },
        totalPages: {
            type: Number,
            default: 0,
        },
        currentPage: {
            type: Number,
            default: PAGINATION.DEFAULT_PAGE,
        },
        itemsPerPage: {
            type: Number,
            default: PAGINATION.DEFAULT_LIMIT,
        },
        testIdPrefix: {
            type: String,
            required: true,
        },
        shimmerSlots: {
            type: Array,
            default: () => ['indexno', 'period_detail', 'created_at', 'status', 'action'],
        },
        dataSlots: {
            type: Array,
            default: () => ['period_detail', 'created_at', 'status', 'action'],
        },
    },
    
    computed: {
        /**
         * Generates shimmer/skeleton placeholder items for loading state
         * @returns {Array} Array of placeholder items for skeleton loader display
         */
        shimmerItems() {
            return Array.from({ length: 5 }, () => ({
                indexno: '',
                period_detail: '',
                created_at: '',
                status: '',
                role: '',
                action: '',
            }));
        },
    },
    
    methods: {
        /**
         * Formats ISO date string to "4th Dec - 2025, 1:36pm" format
         * @param {String} dateString - ISO date string
         * @returns {String} Formatted date string
         */
        formatDate(dateString) {
            if (!dateString) return '';
            
            const date = new Date(dateString);
            
            const day = date.getDate();
            const ordinal = (day) => {
                const s = ['th', 'st', 'nd', 'rd'];
                const v = day % 100;
                return day + (s[(v - 20) % 10] || s[v] || s[0]);
            };
            
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[date.getMonth()];
            
            const year = date.getFullYear();
            
            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'pm' : 'am';
            hours = hours % 12 || 12;
            
            return `${ordinal(day)} ${month} - ${year}, ${hours}:${minutes}${ampm}`;
        },
    },
};
</script>

<style scoped>
.report-table {
    box-shadow: 0px 4px 4px 0px #00000014 !important;
}

/* Fixed width container to maintain consistent button positions */
.action-btn-container {
    width: 24px;
    min-width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
